import { getTrackerByUserId, updateTrackerByOrderId } from "@/utils/apis/dna-test-tracker"
import { useAuthStore } from "@/utils/stores/useAuthStore"
import { useTrackersStore } from "@/utils/stores/useTrackersStore"
import { useCallback, useState } from "react"
import { DnaTestTrackingCategory } from "../types/DnaTestTrackingCategory"
import { DnaTestTrackingStatus } from "../types/DnaTestTrackingStatus"

export const useDnaTracker = () => {
    const { trackers, updateTrackers } = useTrackersStore()
    const { auth } = useAuthStore();

    const dnaTracker = trackers.dnaStatus
    const dnaOrderId = trackers.dnaOrderId
    const userId = auth.userId
    const idToken = auth.idToken;

    const [isLoading, setIsLoading] = useState(false);

    const fetchDnaTracker = useCallback(async () => {
        if (!userId || !idToken) return;
        try {
            const trackers = await getTrackerByUserId(userId, idToken)
            if (trackers) {
                console.log("[DNA Tracker] Updating status from API");
                const { tracking_status, order_id } = trackers[0];
                updateTracker(tracking_status, order_id)
            } else {
                console.warn(`[DNA Tracker] No tracker found for user: ${userId}`);
            }
        } catch (error) {
            console.error(
                "[DNA Tracker] Error fetching tracker:",
                error instanceof Error ? error.message : error
            );
        }
    }, [userId, idToken, dnaTracker, updateTrackers])

    const updateTracker = useCallback(
        (newStatus: DnaTestTrackingStatus, orderId: string, onSuccess?: () => void) => {
            if (newStatus === dnaTracker) {
                console.log(`[DNA Tracker] No update needed: status is already "${dnaTracker}"`)
                return
            }

            if (!orderId || !idToken) {
                console.error("[DNA Tracker] Missing order ID or token, cannot update tracker")
                return
            }

            console.log(`[DNA Tracker] Updating tracker to "${newStatus}"...`)
            setIsLoading(true)

            updateTrackerByOrderId(
                orderId,
                idToken,
                newStatus,
                (tracker) => {
                    const updatedStatus = tracker.tracking_status;
                    updateTrackers({ dnaStatus: updatedStatus, dnaOrderId: orderId });
                    console.log(`[DNA Tracker] Successfully updated to "${tracker.tracking_status}"`)

                    if (onSuccess) onSuccess();
                },
                (error) => {
                    console.error("[DNA Tracker] Failed to update tracker:", error)
                }
            ).finally(() => {
                setIsLoading(false) // ✅ Ensures loading state resets even if the API call fails
            })
        }, [dnaTracker, idToken, updateTrackers]
    )

    const getDnaTestCategory = () => {
        if (dnaTracker === DnaTestTrackingStatus.ReportGenerated) {
            return DnaTestTrackingCategory.ResultsReady
        }
        if (
            dnaTracker === DnaTestTrackingStatus.Departed ||
            dnaTracker === DnaTestTrackingStatus.DeliveredToLaboratory ||
            dnaTracker === DnaTestTrackingStatus.StartingToExtract ||
            dnaTracker === DnaTestTrackingStatus.StartingAnalysis ||
            dnaTracker === DnaTestTrackingStatus.AnalysisDone
        ) {
            return DnaTestTrackingCategory.InLaboratory
        }
        return DnaTestTrackingCategory.Home
    }

    return { dnaTracker, dnaOrderId, isLoading, updateTracker, fetchDnaTracker, getDnaTestCategory }
}